import React, { useState } from "react";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import { useShortlist } from "../lib/shortlist";
import { formatApiError } from "../lib/api";

/**
 * v77.31 — Verified-employer-only bookmark button for Open-for-Work
 * listings. Renders nothing for non-employers so guest/seller tiles stay
 * uncluttered. Filled icon = saved; outline = save.
 *
 * Variants:
 *   `default` — pill chip ("Save candidate" / "Saved") for the candidate
 *               detail page and candidate-search results.
 *   `compact` — icon-only square button for tight grid corners (browse /
 *               home Open-for-Work tiles).
 */
export default function SaveCandidateButton({ listingId, variant = "compact", className = "" }) {
    const { enabled, isSaved, toggle } = useShortlist();
    const [busy, setBusy] = useState(false);

    if (!enabled || !listingId) return null;

    const saved = isSaved(listingId);

    const onClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
            const nextSaved = await toggle(listingId);
            toast.success(nextSaved ? "Candidate saved to shortlist" : "Removed from shortlist");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (variant === "compact") {
        return (
            <button
                type="button"
                data-testid={`save-candidate-${listingId}`}
                aria-pressed={saved}
                title={saved ? "Remove from shortlist" : "Save candidate"}
                onClick={onClick}
                disabled={busy}
                className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
                    saved
                        ? "bg-aduku-navy text-aduku-yellow"
                        : "bg-white/85 hover:bg-white text-aduku-navy/70 hover:text-aduku-navy border border-aduku-border"
                } ${className}`}
            >
                {saved
                    ? <BookmarkCheck className="w-4 h-4" />
                    : <Bookmark className="w-4 h-4" />}
            </button>
        );
    }

    return (
        <button
            type="button"
            data-testid={`save-candidate-${listingId}`}
            aria-pressed={saved}
            onClick={onClick}
            disabled={busy}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                saved
                    ? "bg-aduku-navy text-aduku-yellow hover:bg-aduku-navy-700"
                    : "bg-white border border-aduku-navy text-aduku-navy hover:bg-aduku-navy hover:text-white"
            } ${className}`}
        >
            {saved
                ? <><BookmarkCheck className="w-3.5 h-3.5" /> Saved</>
                : <><Bookmark className="w-3.5 h-3.5" /> Save candidate</>}
        </button>
    );
}
